import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { DeviceMobile, SignOut } from "@phosphor-icons/react";

export function MySessionsDialog() {
  const { logout } = useAuth();
  const navigateToLogin = () => { logout(); window.location.href = "/login"; };
  const [open, setOpen] = useState(false);
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rc, setRc] = useState(null); // {remaining,total}
  const [newCodes, setNewCodes] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/me/devices"); setDevices(data.devices || []); }
    catch (e) { toast.error(formatApiError(e)); }
    try { const { data } = await api.get("/me/recovery-codes"); setRc(data); } catch (e) { /* ignore */ }
  };
  const regenerate = async () => {
    if (!window.confirm("Régénérer vos codes de secours ? Les anciens codes ne fonctionneront plus.")) return;
    try { const { data } = await api.post("/me/recovery-codes/regenerate"); setNewCodes(data.recovery_codes || []); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const onOpenChange = (v) => { setOpen(v); if (v) load(); };
  const fdate = (iso) => (iso ? iso.slice(0, 16).replace("T", " ") : "—");
  const revokeOne = async (id) => {
    if (!window.confirm("Révoquer cet appareil ? Il devra se reconnecter.")) return;
    try { const { data } = await api.post(`/me/devices/${id}/revoke`); toast.success(`Appareil révoqué${data.label ? ` : ${data.label}` : ""}.`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const logoutAll = async () => {
    if (!window.confirm("Se déconnecter de tous les appareils ? Vous devrez vous reconnecter partout, y compris ici.")) return;
    setBusy(true);
    try { await api.post("/me/logout-all"); toast.success("Déconnexion de toutes les sessions effectuée."); setTimeout(navigateToLogin, 800); }
    catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="open-sessions-dialog" className="w-full justify-start text-muted-foreground hover:text-foreground mb-1">
          <DeviceMobile size={16} className="mr-2" /> Mes appareils
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="my-sessions-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Mes appareils connectés</DialogTitle>
          <DialogDescription>Appareils depuis lesquels votre compte s'est connecté récemment. Révoquez ceux que vous ne reconnaissez pas.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2 max-h-[320px] overflow-y-auto" data-testid="my-sessions-list">
          {devices.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">Aucun appareil connu.</div>}
          {devices.map((d) => (
            <div key={d.id} className="flex items-center gap-3 rounded-md border border-border p-2.5" data-testid={`my-device-${d.id}`}>
              <DeviceMobile size={18} className="text-primary shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{d.label}</div>
                <div className="text-[11px] text-muted-foreground font-mono truncate">
                  {d.location ? `${d.location} · ` : ""}{d.ip || "IP inconnue"}
                </div>
                <div className="text-[11px] text-muted-foreground font-mono">Dernier accès : {fdate(d.last_seen)}</div>
              </div>
              <button onClick={() => revokeOne(d.id)} data-testid={`my-device-revoke-${d.id}`} className="text-[11px] text-destructive hover:underline shrink-0">Révoquer</button>
            </div>
          ))}
        </div>
        <div className="border-t border-border pt-3 mt-1" data-testid="recovery-section">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-medium">Codes de secours</div>
              <div className="text-[11px] text-muted-foreground">{rc ? `${rc.remaining} code(s) restant(s) sur ${rc.total}` : "…"} — utiles si l'e-mail 2FA n'arrive pas.</div>
            </div>
            <Button variant="outline" size="sm" onClick={regenerate} data-testid="recovery-regenerate-button">Régénérer</Button>
          </div>
          {newCodes && (
            <div className="mt-2 grid grid-cols-2 gap-1.5 p-3 rounded-md border border-border bg-muted/40 font-mono text-xs" data-testid="recovery-new-codes">
              {newCodes.map((c, i) => <div key={i} className="tracking-wider">{c}</div>)}
              <div className="col-span-2 text-[11px] text-muted-foreground mt-1">Copiez et conservez ces codes maintenant — ils ne seront plus affichés.</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="destructive" onClick={logoutAll} disabled={busy} data-testid="logout-all-button">
            <SignOut size={15} className="mr-1.5" /> {busy ? "…" : "Se déconnecter partout"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
